import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';

import { Button } from '@/components/atoms/Button';
import { cn } from '@/components/atoms/primitives/utils';

/**
 * WEB-1005 — the open listbox of a custom (Radix) select, skinned with the DS
 * 1.9.0 menu surface (`.m-menu`, operator ruling 2026-09-29).
 *
 * All paint lives in `styles/components/molecules.css` (`.m-menu`,
 * `.m-menu__item`); this file only wires Radix, rendering it directly so no
 * Tailwind utility (utilities layer) out-ranks the component-layer skin.
 *
 * Compose `SelectMenu` (root) > `SelectMenuTrigger` (+ `SelectMenuValue`) >
 * `SelectMenuContent` > `SelectMenuItem`. DS 1.9.0 has no frame for a
 * custom-listbox trigger (only the native `.a-select`); per the WEB-1020
 * operator ruling (2026-09-30) it is the DS `Button` `ghost` md with a chevron.
 */
export const SelectMenu = SelectPrimitive.Root;
export const SelectMenuValue = SelectPrimitive.Value;

export type SelectMenuTriggerProps = Omit<
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
  'asChild'
>;

/**
 * The listbox trigger: the Radix `Trigger` rendered through the DS `Button`
 * (`ghost`, md 40; the coarse-pointer 44 floor is the button's own `::before`,
 * never width-keyed). `className` is for layout and label type only.
 */
export const SelectMenuTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectMenuTriggerProps
>(function SelectMenuTrigger({ children, className, ...props }, ref) {
  return (
    <SelectPrimitive.Trigger ref={ref} asChild {...props}>
      <Button variant="ghost" className={className}>
        {children}
        <SelectPrimitive.Icon asChild>
          <ChevronDownIcon aria-hidden="true" className="size-4 opacity-60" />
        </SelectPrimitive.Icon>
      </Button>
    </SelectPrimitive.Trigger>
  );
});
export const SelectMenuContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(function SelectMenuContent(
  { className, children, position = 'popper', sideOffset = 4, ...props },
  ref,
) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        sideOffset={sideOffset}
        className={cn('m-menu', className)}
        {...props}
      >
        <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});

/** One option: `[data-highlighted]` = DS item hover, checked = `--accent-text` + check. */
export const SelectMenuItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(function SelectMenuItem({ className, children, ...props }, ref) {
  return (
    <SelectPrimitive.Item ref={ref} className={cn('m-menu__item', className)} {...props}>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="m-menu__check">
        <CheckIcon aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
});
